import { useAuth } from '@/hooks/useAuth';
import {
    Calendar,
    ChevronUp,
    Clock,
    Heart,
    MapPin,
    MessageSquare,
    Phone,
    Plus,
    Search,
    Share2,
    Star,
    X
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface QuickAction {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
  color: string;
  shortcut?: string;
  category: 'primary' | 'secondary' | 'utility';
}

const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  // Detectar se está em mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Compartilhar aplicação
  const shareApp = useCallback(async () => {
    const shareData = {
      title: 'TaMoAki - Serviços ao Domicílio',
      text: 'Encontre prestadores qualificados para diversos serviços em Angola',
      url: window.location.origin,
    };

    try {
      if (navigator.share && isMobile) {
        await navigator.share(shareData);
        toast.success('Aplicação compartilhada com sucesso!');
      } else {
        // Fallback para desktop
        await navigator.clipboard.writeText(shareData.url);
        toast.success('Link copiado para a área de transferência!');
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
      toast.error('Erro ao compartilhar aplicação');
    }
  }, [isMobile]);

  // Ações primárias (sempre visíveis)
  const primaryActions: QuickAction[] = useMemo(() => [
    {
      id: 'new-appointment',
      label: 'Novo Agendamento',
      icon: Plus,
      action: () => navigate('/categorias'),
      color: 'bg-orange-500 hover:bg-orange-600',
      shortcut: 'N',
      category: 'primary'
    },
    {
      id: 'search-services',
      label: 'Buscar Serviços',
      icon: Search,
      action: () => navigate('/categorias'),
      color: 'bg-blue-500 hover:bg-blue-600',
      shortcut: 'S',
      category: 'primary'
    },
    {
      id: 'my-appointments',
      label: 'Meus Agendamentos',
      icon: Calendar,
      action: () => navigate('/appointments'),
      color: 'bg-green-500 hover:bg-green-600',
      shortcut: 'A',
      category: 'primary'
    },
    {
      id: 'messages',
      label: 'Mensagens',
      icon: MessageSquare,
      action: () => navigate('/messages'),
      color: 'bg-purple-500 hover:bg-purple-600',
      shortcut: 'M',
      category: 'primary'
    }
  ], [navigate]);

  // Ações secundárias (visíveis quando expandido)
  const secondaryActions: QuickAction[] = useMemo(() => [
    {
      id: 'favorites',
      label: 'Favoritos',
      icon: Heart,
      action: () => navigate('/favorites'),
      color: 'bg-red-500 hover:bg-red-600',
      shortcut: 'F',
      category: 'secondary'
    },
    {
      id: 'reviews',
      label: 'Avaliações',
      icon: Star,
      action: () => navigate('/avaliacoes'),
      color: 'bg-yellow-500 hover:bg-yellow-600',
      shortcut: 'R',
      category: 'secondary'
    },
    {
      id: 'nearby',
      label: 'Prestadores Próximos',
      icon: MapPin,
      action: () => navigate('/explore?filter=nearby'),
      color: 'bg-indigo-500 hover:bg-indigo-600',
      shortcut: 'P',
      category: 'secondary'
    },
    {
      id: 'recent',
      label: 'Serviços Recentes',
      icon: Clock,
      action: () => navigate('/explore?filter=recent'),
      color: 'bg-teal-500 hover:bg-teal-600',
      shortcut: 'T',
      category: 'secondary'
    }
  ], [navigate]);

  // Ações utilitárias
  const utilityActions: QuickAction[] = useMemo(() => [
    {
      id: 'contact-support',
      label: 'Suporte',
      icon: Phone,
      action: () => navigate('/ajuda'),
      color: 'bg-gray-500 hover:bg-gray-600',
      shortcut: 'H',
      category: 'utility'
    },
    {
      id: 'share-app',
      label: 'Compartilhar',
      icon: Share2,
      action: () => shareApp(),
      color: 'bg-pink-500 hover:bg-pink-600',
      shortcut: 'C',
      category: 'utility'
    }
  ], [navigate, shareApp]);

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignorar se estiver em um input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const key = e.key.toLowerCase();
      const allActions = [...primaryActions, ...secondaryActions, ...utilityActions];
      const action = allActions.find(a => a.shortcut?.toLowerCase() === key);

      if (action) {
        e.preventDefault();
        action.action();
        toast.success(`Ação rápida: ${action.label}`);
      }

      // Atalho para expandir/recolher
      if (e.key === 'Escape') {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [primaryActions, secondaryActions, utilityActions]);

  const renderAction = (action: QuickAction) => {
    const Icon = action.icon;
    return (
      <button
        key={action.id}
        onClick={action.action}
        className={`${action.color} text-white p-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white`}
        title={`${action.label} (${action.shortcut})`}
      >
        <div className="flex flex-col items-center space-y-1">
          <Icon className="w-6 h-6" />
          <span className="text-xs font-medium">{action.label}</span>
        </div>
      </button>
    );
  };

  if (!user) return null;

  return (
    <div className="fixed bottom-20 md:bottom-4 right-4 z-40">
      {/* Botão principal */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
        aria-label={isExpanded ? 'Fechar ações rápidas' : 'Abrir ações rápidas'}
        aria-expanded={isExpanded}
      >
        {isExpanded ? (
          <X className="w-6 h-6" />
        ) : (
          <ChevronUp className="w-6 h-6" />
        )}
      </button>

      {/* Painel de ações */}
      {isExpanded && (
        <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl p-4 w-80 max-w-[calc(100vw-1.5rem)]">
          <div className="space-y-4">
            {/* Ações primárias */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Ações Principais</h3>
              <div className="grid grid-cols-2 gap-2">
                {primaryActions.map(renderAction)}
              </div>
            </div>

            {/* Ações secundárias */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Explorar</h3>
              <div className="grid grid-cols-2 gap-2">
                {secondaryActions.map(renderAction)}
              </div>
            </div>

            {/* Ações utilitárias */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Utilitários</h3>
              <div className="grid grid-cols-2 gap-2">
                {utilityActions.map(renderAction)}
              </div>
            </div>

            {/* Dica de atalhos */}
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              💡 Use as teclas N, S, A, M, F, R, P, T, H, C para atalhos rápidos
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActions; 